import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { IsAccountValidGuard } from './base/is-account-valid.guard';
import { LoadServicesGuard } from './base/load-services.guard';
import { isPlatformBrowser } from '@angular/common';
import { SEOService } from '../services/seo.service';
import { RedirectionService } from '../services/redirection.service';
import { DomainRedirectionGuard } from './base/domain-redirection.guard';
import { DomainSigninRedirectionGuard } from './base/domain-signin-redirection.guard';
import { LastActiveTeamGuard } from './last-active-team.guard';
import { IsAccountPremiumGuard } from './base/is-account-premium.guard';
import { ResolveSubscriptionService } from '../services/resolvers/resolve-subscription.service';
import { Constant } from 'src/constant';
import { ResolveDomainService } from '../services/resolvers/resolve-domain.service';
import { redirectToMainDomain } from '@app/shared/helpers/location';


@Injectable({
  providedIn: 'root'
})
export class UploaderGuard {

  constructor(
    private loadServicesGuard: LoadServicesGuard,
    private isAccountValidGuard: IsAccountValidGuard,
    private seoService: SEOService,
    private domainRedirectionGuard: DomainRedirectionGuard,
    private domainSigninRedirectionGuard: DomainSigninRedirectionGuard,
    private redirectionService: RedirectionService,
    private isAccountPremiumGuard: IsAccountPremiumGuard,
    private lastActiveTeamGuard: LastActiveTeamGuard,
    private resolveSubscriptionService: ResolveSubscriptionService,
    private resolveDomain: ResolveDomainService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  canActivate(): Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      return this.loadServicesGuard.canActivate().pipe(mergeMap((servicesLoaded: boolean) => {
        // Deliberately no `else`: LoadServicesGuard.canActivate() never emits `false` today
        // (see base/load-services.guard.ts), so this branch is unreachable. Revisit if that
        // ever changes.
        if (servicesLoaded) {
          return this.isAccountValidGuard.canActivate().pipe(mergeMap((isAccountValid: boolean) => {
            // Deliberately no `else`: IsAccountValidGuard.canActivate() never emits `false`
            // today (see base/is-account-valid.guard.ts), so this branch is unreachable.
            // Revisit if that ever changes.
            if (isAccountValid) {
              return this.isAccountPremiumGuard.canActivate().pipe(mergeMap((isLoggedIn: boolean) => {
                if (isLoggedIn) {
                  return this.resolveSubscriptionService.resolve()
                    .pipe(mergeMap(({ subscription, isRoot, isAdmin, language }) => {
                      switch (subscription?.status) {
                        case Constant.subscriptionStatus.PaymentRequired:
                          this.redirectionService.redirectToSubscriptionUpdate();
                          break;
                        case Constant.subscriptionStatus.Inactive:
                          this.redirectionService.redirectToPricingPage(language);
                          break;
                        default:
                          break;
                      }

                      return this.lastActiveTeamGuard.canActivate().pipe(mergeMap((activeTeam: boolean) => {
                        if (activeTeam) {
                          return this.resolveDomain.resolve().pipe(
                            mergeMap(() => this.domainRedirectionGuard.canActivate()),
                            catchError(() => {
                              if (isPlatformBrowser(this.platformId)) { redirectToMainDomain(); }
                              return of(false);
                            }),
                          );
                        } else if (isRoot || isAdmin) {
                          this.redirectionService.redirectToSignupDomain();
                          return of(false);
                        } else {
                          this.redirectionService.redirectToInvalidConfiguration();
                          return of(false);
                        }
                      }));
                    }));

                } else {
                  return this.resolveDomain.resolve()
                    .pipe(
                      mergeMap(() => this.domainRedirectionGuard.canActivate().pipe(mergeMap((domainChecked: boolean) => {
                        if (domainChecked) {
                          return this.domainSigninRedirectionGuard.canActivate();
                        } else {
                          return of(domainChecked);
                        }
                      }))),
                      catchError(() => {
                        if (isPlatformBrowser(this.platformId)) { redirectToMainDomain(); }
                        return of(false);
                      })
                    );
                }
              }));
            }
          }));
        }
      }));
    } else {
      this.seoService.setMetasLanding();
      return of(true);
    }
  }
}
